import { useContext, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import { Box, Dialog, SelectChangeEvent, useMediaQuery } from '@mui/material';
import { v4 as uuid } from 'uuid';

import { ingredientsObjectSelector } from '../../../app/commonSelector';
import { GET_INGREDIENTS, GET_RECIPE_E3_FROM_ID, GET_RECIPE_PRESETS } from '../../../app/graphql/queries';
import {
  setCreateSubstepOpen,
  setSubstepToEdit,
} from '../../../app/recipeCreation/createRecipe.slice';
import {
  Ingredients,
  InsideIngredient,
  RecipePresetSubstep,
  SubstepIngredient,
  SubstepsIngredientsE3s,
} from '../../../app/recipeCreation/createRecipe.types';
import { initialSubstep } from '../../../app/recipeCreation/createRecipe.utils';
import {  setRecipeAndIngredients } from '../../../app/recipePage/recipePage.slice';
import { useAppDispatch, useAppSelector } from '../../../app/store/hooks';
import useLogRecipeEdit from '../../../hooks/useLogRecipeEdit';
import useSaveSubstep from '../../../hooks/useSaveSubstep';
import { ErrorManagementContext } from '../../../utils/errorManagement/ErrorManagementProvider';
import { EventLogsEnum } from '../../../utils/events';
import { getIngredientsData } from '../../../utils/experience2/ingredientsUtil';
import { recipeE3TransformerQL2FS } from '../../../utils/RecipeBackEnd/hasuraFSMigrationTransformers';
import StepsHeader from '../stepsHeader';
import {
  COOKING_NOTES_PLACEHOLDER,
  LocationEnum,
  SubStepEditEnum,
  SubStepTypeEnum,
  TOOLBAR_OPTIONS,
} from '../utils/constants';
import { ingredientsData, recipePresets } from '../utils/createSubstep.utils';
import IngredientCreate from './IngredientCreation';
import InstructionCreate from './InstructionCreation';
import 'react-quill/dist/quill.snow.css';
import './quillstyle.css';
import LocationSelector from './LocationSelector';
import QuillEditor from './QuillEditor';
import SubStepTypeSelector from './SubStepTypeSelector';

const instructionTypeSelect: Record<string, string> = {
  instruction: SubStepTypeEnum.INSTRUCTION,
  ingredients: SubStepTypeEnum.INGREDIENTS,
  outsideInstruction: SubStepTypeEnum.OUTSIDE_INSTRUCTION,
};

const getEditedIngredients = (newIngredientsData: Array<SubstepsIngredientsE3s>, existingIngredientsData: Array<SubstepIngredient>, substepId: string) => {
  const newIngredient: SubstepIngredient[] = [];
  newIngredientsData.forEach((i: any) => {
    const existingIngredient = existingIngredientsData.find((id: SubstepIngredient) => id.ingredient_id === i.ingredients[0].ingredientId);
    if (existingIngredient && existingIngredient.subStep_id === substepId) {
      newIngredient.push(existingIngredient)
    } else {
      newIngredient.push({
        id: uuid(),
        subStep_id: substepId,
        ingredient_id: i.ingredients[0].ingredientId
      });
    }
  });
  return newIngredient;
};

const CreateSubstep = ({
  stepId,
  stepAdded = false,
  substeps,
  substepId,
  subStepUId,
  setIsStepAdded
}: {
  stepId?: string | null;
  stepAdded?: boolean;
  substeps?: any;
  substepId?: string | null;
  subStepUId?: string | null;
  substepIngredients?: InsideIngredient;
  setIsStepAdded?: React.Dispatch<React.SetStateAction<boolean>> | undefined
}) => {
  const dispatch = useAppDispatch();
  const { onSave, loading: saving } = useSaveSubstep();
  const [fetchRecipe, { loading: recipeLoading }] = useLazyQuery(GET_RECIPE_E3_FROM_ID, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });
  const { cookClubLog } = useContext(ErrorManagementContext);
  const [fetchIngredients, { loading: fetchingIngredients }] = useLazyQuery(GET_INGREDIENTS, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  })

  const user = useAppSelector((state) => state.main.user);
  const recipeData = useAppSelector((state) => state.recipeCreation.recipeData);
  const subStep = useAppSelector((state) => state.recipeCreation.subStepToEdit);
  const substepV2 = useAppSelector((state) => state.recipeCreation.initialSubstep);
  const oldRecipe = useAppSelector((state) => state.recipeCreation.oldRecipeData);
  const substepIndex = useAppSelector((state) => state.recipeCreation.substepIndex);
  const ingredientsObject = useAppSelector(ingredientsObjectSelector);
  const recipeVariationId = oldRecipe?.Recipe_Variations[0].id;
  const substepOperation = useAppSelector((state) => state.recipePage.substepEdit.substepOperation);
  const isSubstepAdded = substepOperation === SubStepEditEnum.ADD_SUBSTEP;
  const { logRecipeEdit } = useLogRecipeEdit();
  const createSubstepOpen = useAppSelector(state => state.recipeCreation.createSubstepOpen);

  const { subStep_id } = subStep;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { parameters, name, type, id, Recipe_Presets } = substepV2 ?? { parameters: null, name: null, type: null, id: null }

  const [titleText, setTitleText] = useState(name);
  const [instructionType, setInstructionType] = useState<string>(type ? instructionTypeSelect[type] : SubStepTypeEnum.OUTSIDE_INSTRUCTION);
  const [location, setLocation] = useState(
    type === SubStepTypeEnum.OUTSIDE_INSTRUCTION
      ? parameters.location.toLowerCase()
      : LocationEnum.JAR
  );
  const [descText, setDescText] = useState(parameters?.outsideDescription ?? '');
  const [settings, setSettings] = useState(parameters?.settings ?? []);
  const [ingredients, setIngredients] = useState<Array<Ingredients>>(
    substepV2?.parameters.items?.map((ing: Ingredients) =>
      substepV2.type === SubStepTypeEnum.INGREDIENTS ? getIngredientsData(ing, ingredientsObject) : {}
    ) ?? []
  );
  const pre: any = Recipe_Presets?.[0] ?? null;
  const [presetSelected, setPresetSelected] = useState<RecipePresetSubstep | null>(pre);
  const [isPresetOpen, setIsPresetOpen] = useState(!!presetSelected?.id || settings?.length === 0);

  const isMediumScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('md')
  );

  const handleClose = () => {
    // TODO - make 1 dispatch
    dispatch(setCreateSubstepOpen(false));
    dispatch(setSubstepToEdit(initialSubstep));
  };

  const substepParameters = (newSubstepID: string, ingredientList: Array<Ingredients>, getIngredientsList: any) => {
    const { newIngredientsList } = ingredientsData(newSubstepID, ingredientList, user);
    if (instructionType === SubStepTypeEnum.INSTRUCTION)
      return { settings }
    if (instructionType === SubStepTypeEnum.OUTSIDE_INSTRUCTION)
      return {
        location,
        outsideDescription: descText
      }
    if (instructionType === SubStepTypeEnum.INGREDIENTS)
      return {
        items: getIngredientsList, newIngredientsList,
        location
      }
  }

  const onCompleted = async () => {
    const fetchedData = await fetchRecipe({
      variables: {
        id: oldRecipe?.id,
      }
    });
    const newRecipeData = fetchedData.data.Recipes_E3_by_pk;

    const fetchedIngredients = await fetchIngredients();
    dispatch(setRecipeAndIngredients({ recipe: recipeE3TransformerQL2FS(newRecipeData), newRecipedata: fetchedData.data.Recipes_E3_by_pk, ingredients: fetchedIngredients.data.Ingredients_DB ?? [] }))
    if (setIsStepAdded) setIsStepAdded(false);
  }

  const onDoneClick = async () => {
    logRecipeEdit(EventLogsEnum.APP_SUBSTEP_SAVED, {
      eventData: { substepId }
    })

    const existingSubstep = substeps?.find(
      (item: any) => item.subStepUId === subStepUId
    );

    const newSubstepId = () => {
      // For Presets
      if (instructionType === SubStepTypeEnum.INSTRUCTION && !!presetSelected?.id) return presetSelected.subStepId;
      // Mode - EDIT & previous substep is not preset
      if ((!stepAdded && !isSubstepAdded && !existingSubstep?.Recipe_Presets[0]?.id)) return existingSubstep?.id;
      // Mode - ADD
      return uuid();
    }

    const newSubstepID = newSubstepId();
    const { ingredients: getIngredientsList } = ingredientsData(newSubstepID, ingredients, user);
    const createNewSubStep = () => {
      const substep = {
        id: newSubstepID,
        name: titleText ?? "",
        media: { url: "", loop: true, muted: true, title: "", duration: 0, startTime: 0 },
        created_at: stepAdded ? new Date().toISOString() : existingSubstep?.created_at,
        updated_at: new Date().toISOString(),
        type: instructionType,
        parameters: substepParameters(newSubstepID, ingredients, getIngredientsList),
      }

      // if preset is selected
      if (instructionType === SubStepTypeEnum.INSTRUCTION && presetSelected?.id) {
        return { ...substep, Recipe_Presets: recipePresets(presetSelected), SubSteps_Ingredients_E3s: [] }
      }

      // if ingredient is selected
      if (instructionType === SubStepTypeEnum.INGREDIENTS && presetSelected !== null) {
        return { ...substep, Recipe_Presets: [], SubSteps_Ingredients_E3s: getEditedIngredients(getIngredientsList, existingSubstep?.SubSteps_Ingredients_E3s, newSubstepID) };
      }

      if (instructionType === SubStepTypeEnum.INGREDIENTS) {
        return { ...substep, Recipe_Presets: [], SubSteps_Ingredients_E3s: getEditedIngredients(getIngredientsList, existingSubstep?.SubSteps_Ingredients_E3s, newSubstepID) };
      }
      return { ...substep, Recipe_Presets: [], SubSteps_Ingredients_E3s: [] }
    }

    const newSubstep = createNewSubStep();

    if (oldRecipe && recipeVariationId && stepId && newSubstep && subStepUId) {
      await onSave({
        oldRecipe,
        recipeVariationId,
        stepId: stepAdded ? uuid() : stepId,
        oldSubstep: existingSubstep,
        newSubstep,
        addSubstep: isSubstepAdded,
        addSubStepAt: substepIndex + 1,
        initializeStep: stepAdded,
        subStepUId,
        onCompleted,
        onError: (err: any) => {
          cookClubLog(`Something went wrong. Try Again!`, "", {}, { ui: { type: "info" }, browserConsole: true });
        },
      });
    }
    dispatch(setCreateSubstepOpen(false));
  };

  const handleLocationSelect = (event: SelectChangeEvent) => {
    setLocation(event.target.value as string);
  };

  const { loading, error, data, refetch } = useQuery(GET_RECIPE_PRESETS, {
    onError: (err) =>
      cookClubLog(
        `Fetching Recipe Presets Failed`,
        'ERROR_ON_ACCESS_RECIPE_PRESETS',
        { err },
        { ui: { type: 'error' }, browserConsole: true }
      ),
  });
  const presetsData = data?.Recipe_Presets;
  return (
    <Dialog
      fullScreen
      onClose={handleClose}
      open={createSubstepOpen}
      PaperProps={{
        style: {
          display: 'flex',
          justifyContent: 'flex-start',
          width: isMediumScreen ? '100%' : '60%',
        },
      }}
    >
      <>
        <StepsHeader
          buttonLoading={saving || recipeLoading || fetchingIngredients}
          buttonText="Done"
          onButtonClick={onDoneClick}
          onClickBack={handleClose}
          text="Add Step"
        />
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
            boxSizing: 'border-box',
            gap: '30px',
            mt: 2,
          }}
        >
        
          <SubStepTypeSelector
            instructionType={instructionType}
            setInstructionType={setInstructionType}
          />
          {instructionType === SubStepTypeEnum.INSTRUCTION && (
            <InstructionCreate
              setSettings={setSettings}
              settings={settings}
              presetsData={presetsData}
              presetSelected={presetSelected}
              setPresetSelected={setPresetSelected}
              isPresetOpen={isPresetOpen}
              setIsPresetOpen={setIsPresetOpen}
            />
          )}
          {instructionType === SubStepTypeEnum.INGREDIENTS && (
            <IngredientCreate
              ingredients={ingredients}
              setIngredients={setIngredients}
            />
          )}
          {instructionType === SubStepTypeEnum.OUTSIDE_INSTRUCTION && (
            <QuillEditor
              modules={{ toolbar: TOOLBAR_OPTIONS }}
              onChange={(e) => setDescText(e)}
              placeholder={COOKING_NOTES_PLACEHOLDER}
              theme="snow"
              value={descText}
            />
          )}
          {!(instructionType === SubStepTypeEnum.INSTRUCTION && isPresetOpen) && <LocationSelector
            handleLocationSelect={handleLocationSelect}
            instructionType={instructionType}
            location={location}
            setInstructionType={setInstructionType}
            setLocation={setLocation}
            setTitleText={setTitleText}
            titleText={titleText}
          />}
        </Box>
      </>
    </Dialog>
  )
};

export default CreateSubstep;
